import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Modal, Button } from "react-bootstrap";
import toast from "react-hot-toast";
import LineItemsEditor from "../../../../stock/components/LineItemsEditor";
import { useCreateQuotationPrototypeMutation } from "../../quotationsApi";
import { useGetBranchesQuery } from "../../../../admin/adminApi";
import { useClaims } from "../../../../auth/useClaims";
import { paths } from "../../../../../routes/paths";
import { useDefaultFormBranch } from "../../../../branchFilter/useDefaultFormBranch";

const toLineRequest = (row) => ({
  lineType: row.lineType || "PRODUCT",
  productCode: row.lineType === "FREETEXT" ? null : row.productCode || null,
  description: row.productName || row.productGroupName || "", // a group line with no wording prints as the group name
  qty: Number(row.qty) || 0,
  unit: row.unit || null,
  rate: Number(row.rate) || 0,
  vatPercent: Number(row.vatPercent) || 0,
  // Batch185 - only a free-text line carries a group tag; a product has its own group.
  productGroupId: row.lineType === "FREETEXT" && row.productGroupId ? Number(row.productGroupId) : null,
});

// Modal instead of a page nested in the split-view's detail pane - the
// Line Items table needs real horizontal room (Type/Product/Unit/Qty/Rate/
// VAT%/VAT Amt/Amount columns), which the detail pane is too narrow to
// give it. Full-page Edit still exists separately (PrototypeForm.jsx) - only
// Create moved to a modal, per direct instruction.
const PrototypeCreateModal = ({ show, onClose }) => {
  const navigate = useNavigate();
  const { branches: callerBranchCodes, isAllBranches } = useClaims();
  const { data: branches = [] } = useGetBranchesQuery();
  const [createPrototype, { isLoading: isCreating }] = useCreateQuotationPrototypeMutation();

  const availableBranches = isAllBranches ? branches : branches.filter((b) => callerBranchCodes?.includes(b.branchCode));

  const defaultBranchId = useDefaultFormBranch(availableBranches);
  const [branchId, setBranchId] = useState(defaultBranchId);
  // A new record starts on the header branch (useDefaultFormBranch, 2026-10-02); the
  // branch list loads async, so it is filled in once known unless already chosen.
  useEffect(() => {
    if (!branchId && defaultBranchId !== "") setBranchId(defaultBranchId);
  }, [defaultBranchId]); // eslint-disable-line react-hooks/exhaustive-deps
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [rows, setRows] = useState([]);

  const reset = () => {
    setBranchId(defaultBranchId);
    setName("");
    setDescription("");
    setRows([]);
  };

  const handleClose = () => {
    reset();
    onClose();
  };

  const handleSubmit = async () => {
    if (!branchId) return toast.error("Select a branch");
    if (!name.trim()) return toast.error("Name is required");
    if (rows.length === 0) return toast.error("At least one line item is required");

    try {
      const created = await createPrototype({
        branchId: Number(branchId),
        name: name.trim(),
        description: description.trim() || null,
        lines: rows.map(toLineRequest),
      }).unwrap();
      toast.success("Prototype created");
      reset();
      onClose();
      navigate(paths.quotationPrototypeDetail(created.recordGuid));
    } catch (e) {
      toast.error(e?.message || "Could not save the prototype");
    }
  };

  return (
    <Modal show={show} onHide={handleClose} size="xl" scrollable>
      <Modal.Header closeButton>
        <Modal.Title>New Quotation Prototype</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <div className="row">
          <div className="col-md-4 mb-3">
            <label className="form-label">Branch</label>
            <select className="form-control" value={branchId} onChange={(e) => setBranchId(e.target.value)}>
              <option value="">Select branch</option>
              {availableBranches.map((b) => (
                <option key={b.branchId} value={b.branchId}>{b.branchCode}</option>
              ))}
            </select>
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Name</label>
            <input type="text" className="form-control" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Guarding" />
          </div>
          <div className="col-md-4 mb-3">
            <label className="form-label">Description (optional)</label>
            <input type="text" className="form-control" value={description} onChange={(e) => setDescription(e.target.value)} />
          </div>
        </div>

        <h6 className="mb-3">Line Items</h6>
        <LineItemsEditor
          branchId={branchId ? Number(branchId) : undefined}
          rows={rows}
          onChange={setRows}
          allowFreeText
          rateField="salesRate"
          columns={{ showRate: true, showVat: true, amountIncludesVat: true }}
        />
      </Modal.Body>
      <Modal.Footer>
        <Button variant="secondary" onClick={handleClose}>Cancel</Button>
        <Button variant="primary" onClick={handleSubmit} disabled={isCreating}>
          {isCreating ? "Saving..." : "Save Prototype"}
        </Button>
      </Modal.Footer>
    </Modal>
  );
};

export default PrototypeCreateModal;
